import { useState } from 'react';
import { sortBy } from 'lodash';
import { useMedia } from 'react-use';
import { type PairingMutation } from '@hungryroot/cart';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import { Icon, IconVariant } from '@hungryroot/icons';
import { Paragraph, Button } from '@hungryroot/ramen';
import { type Cart, type PairingV1 as Pairing } from '@hungryroot/rest';
import { useSaladShooter } from '@hungryroot/salad-shooter';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { useCustomization } from '../../../CustomizationProvider/CustomizationProvider';
import { type IngredientViewModel } from '../../../Ingredient/Ingredient';
import {
  PointsWithIcon,
  PointsWithIconVariant,
} from '../../../PointsWithIcon/PointsWithIcon';
import { type UseEditRecipeResult } from '../../hooks/useEditRecipe/useEditRecipe';
import { IngredientEdit as IngredientEditScreen } from '../Screens/IngredientEdit/IngredientEdit';
import { IngredientSwap } from '../Screens/IngredientSwap/IngredientSwap';
import {
  RemovalFeedback,
  type RemovalFeedbackProduct,
} from '../Screens/RemovalFeedback/RemovalFeedback';
import { SheetOrOverlay } from '../SheetOrOverlay/SheetOrOverlay';
import styles from './IngredientEditorModal.module.css';

export enum StateTypes {
  Edit,
  RemovalFeedback,
  Swap,
}

interface StateEdit {
  type: StateTypes.Edit;
}

interface StateFeedback {
  type: StateTypes.RemovalFeedback;
  product: RemovalFeedbackProduct;
}

interface StateSwap {
  type: StateTypes.Swap;
  swappablePairings: Pairing[];
  ingredient: IngredientViewModel;
}

export type State = StateEdit | StateFeedback | StateSwap;

interface Props {
  testid?: string;
  ingredients: IngredientViewModel[];
  swappablePairings?: Record<number, Pairing[]>;
  editRecipeQuery: UseEditRecipeResult;
  overlayClassName?: string;
  initialState?: State;
  isOpen: boolean;
  onDismiss: () => void;
  isModalEditor?: boolean;
  onUpdate?: (
    cart: Cart,
    modifications: PairingMutation[],
    pairing?: Pairing
  ) => void;
  contentClassName?: string;
  // this is used to control the ingredient modal experience specific the new RDP
  isCustomModalEditor?: boolean;
  index?: number | null;
  id: number;
}

const STATE_EDIT: StateEdit = {
  type: StateTypes.Edit,
};

function getScreenTitle(editState: State) {
  switch (editState.type) {
    case StateTypes.Edit:
      return 'Edit Ingredients';
    case StateTypes.Swap:
      return 'Swap Ingredient';
  }
  return undefined;
}

function getEditorModalScreenTitle(editState: State) {
  switch (editState.type) {
    case StateTypes.Edit:
      return 'Edit Recipe';
    case StateTypes.Swap:
      return 'Swap Ingredient';
    case StateTypes.RemovalFeedback:
      return 'Remove Ingredient';
  }
}

const RDPIcons = {
  remove: <Icon variant={IconVariant.X} />,
  add: <Icon variant={IconVariant.PlusCircle} />,
  swap: <Icon variant={IconVariant.Swap} />,
};

const editorIcons = {
  remove: (
    <div className={styles['editor-btn']}>
      <Icon variant={IconVariant.Trash2} />
      <Paragraph variant="caption-xs">Remove</Paragraph>
    </div>
  ),
  add: (
    <div className={styles['editor-btn']}>
      <Icon variant={IconVariant.Plus} />
      <Paragraph variant="caption-xs">Add</Paragraph>
    </div>
  ),
  swap: (
    <div className={styles['editor-btn']}>
      <Icon variant={IconVariant.Swap} />
      <Paragraph variant="caption-xs">Swap</Paragraph>
    </div>
  ),
};

export function IngredientEditorModal(props: Props) {
  const {
    testid,
    ingredients: unsortedIngredients,
    editRecipeQuery,
    overlayClassName,
    initialState,
    isOpen,
    onDismiss,
    isModalEditor = false,
    onUpdate,
    contentClassName,
    isCustomModalEditor,
    // the index here is for salad shooter tracking purposes; however, this has not been implemented
    // every place, so it's set as null by default to signal to the data team the data sent is not
    // part of the for you experience
    index = null,
    id,
  } = props;
  const [editState, setEditState] = useState<State>(
    initialState !== undefined ? initialState : STATE_EDIT
  );
  const [openedState, setOpenedState] = useState<State | null>(
    initialState !== undefined ? initialState : null
  );

  const isDesktop = useMedia(BreakpointQueryLarge);
  const customization = useCustomization();

  const handleUpdate = (
    modifications: PairingMutation[],
    pairing?: Pairing
  ) => {
    if (onUpdate !== undefined && 'cart' in customization) {
      const { cart } = customization;
      onUpdate(cart, modifications, pairing);
    }
  };

  const { toast } = useToast();

  const ingredients = sortBy(unsortedIngredients, ['id']);

  const { pairing, removeIngredient, bundle, swap } = editRecipeQuery;

  const showBackButton =
    (isDesktop && !isModalEditor) ||
    editState.type === StateTypes.Swap ||
    editState.type === StateTypes.RemovalFeedback ||
    isCustomModalEditor;

  const modified = {
    ingredients: bundle.order_details.map(({ product }) => product),
  };

  const screenTitle = isModalEditor
    ? getEditorModalScreenTitle(editState)
    : getScreenTitle(editState);

  const closeModal = () => {
    onDismiss();
    setEditState(STATE_EDIT);
    setOpenedState(null);
  };

  const updateButton = (
    onSelect: () => void,
    points: string,
    disabled?: boolean
  ) => {
    return (
      <div className={styles.update}>
        <Button
          className={styles['update-btn']}
          style={disabled ? 'disabled' : 'default'}
          prominence="primary"
          size="medium"
          buttonType="submit"
          onClick={onSelect}
          textPrimary="Update"
          icon={
            <PointsWithIcon
              points={points}
              variant={PointsWithIconVariant.Large}
              containerClassName={styles['points-with-icon']}
            />
          }
          iconPosition="right"
          width="full-width-mobile"
          testid="editor-update-button"
        />
      </div>
    );
  };

  const icons = isModalEditor ? editorIcons : RDPIcons;
  const modalContentClass =
    isModalEditor && editState.type === StateTypes.Swap ? styles.content : '';
  const modalChildrenClass =
    isModalEditor && editState.type === StateTypes.Swap ? styles.children : '';

  const saladShooter = useSaladShooter();
  return (
    <SheetOrOverlay
      testid="overlay"
      overlayClassName={overlayClassName}
      onBackClick={() => {
        if (
          editState.type === StateTypes.Swap ||
          editState.type === StateTypes.RemovalFeedback
        ) {
          setEditState(STATE_EDIT);
          return;
        }

        closeModal();
      }}
      onRequestDismiss={() => {
        closeModal();
      }}
      title={screenTitle}
      isActive={isOpen || openedState !== null}
      showBackButton={showBackButton}
      isModalEditor={isModalEditor}
      childrenClassName={modalChildrenClass}
      contentClassName={`${modalContentClass} ${contentClassName}`}
    >
      {editState.type === StateTypes.Edit && (
        <IngredientEditScreen
          editRecipeQuery={editRecipeQuery}
          testid={testid}
          modified={modified}
          swappablePairings={swap?.pairings}
          ingredients={ingredients}
          onIngredientAdd={() => {
            !isModalEditor && closeModal();
          }}
          onIngredientRemoveClick={
            removeIngredient &&
            ((ingredient) => {
              saladShooter.track([
                {
                  kind: 'ui-action',
                  action: {
                    kind: 'removing-ingredient',
                    target: { kind: 'pairing', id },
                    position: index,
                    ingredient: {
                      kind: 'product',
                      id: ingredient.id,
                    },
                  },
                },
              ]);
              setEditState({
                type: StateTypes.RemovalFeedback,
                product: ingredient,
              });
            })
          }
          onSwapClick={({ swappablePairings, ingredient }) => {
            saladShooter.track([
              {
                kind: 'ui-action',
                action: {
                  kind: 'swapping-ingredient',
                  target: { kind: 'pairing', id },
                  position: index,
                  ingredient: {
                    kind: 'product',
                    id: ingredient.id,
                  },
                },
              },
            ]);
            setEditState({
              type: StateTypes.Swap,
              swappablePairings,
              ingredient,
            });
          }}
          onBackButtonClick={() => {
            closeModal();
          }}
          isModalEditor={isModalEditor}
          editorIcons={icons}
          onUpdate={handleUpdate}
        />
      )}
      {editState.type === StateTypes.RemovalFeedback && (
        <RemovalFeedback
          isCustomModalEditor={isCustomModalEditor}
          onIngredientWillNever={() => {
            toast(
              <Toast
                testid="toast-marked-as-never"
                variant={ToastVariant.Critical}
              >
                Ingredient marked as 'never'
              </Toast>
            );
          }}
          product={editState.product}
          onReasonSelected={(id, reasonId) => {
            if (removeIngredient === undefined) {
              return;
            }

            const removedWhy = reasonId === 3 ? 'never' : 'dontneed';
            saladShooter.track([
              {
                kind: 'ui-action',
                action: {
                  kind: 'removed-ingredient',
                  target: { kind: 'pairing', id },
                  position: index,
                  ingredient: {
                    kind: 'product',
                    id: editState.product.id,
                  },
                  removed_why: removedWhy,
                },
              },
            ]);
            isModalEditor || isCustomModalEditor
              ? removeIngredient(id, reasonId, handleUpdate)
              : removeIngredient(id, reasonId);
            (!isModalEditor || isCustomModalEditor) && closeModal();
          }}
          testid="removal-feedback"
          isModalEditor={isModalEditor}
          pairingPoints={Number(bundle.points)}
          updateButton={
            isModalEditor && !isCustomModalEditor ? updateButton : undefined
          }
        />
      )}
      {editState.type === StateTypes.Swap && (
        <IngredientSwap
          isCustomModalEditor={isCustomModalEditor}
          ingredient={editState.ingredient}
          swappablePairings={editState.swappablePairings}
          onBackButtonClick={() => {
            setEditState(STATE_EDIT);
          }}
          testid={`${testid}-swap-ingredients`}
          onIngredientSelected={({
            targetPairing,
            sourceIngredientId,
            targetIngredientId,
          }) => {
            if (swap === undefined) {
              return;
            }

            saladShooter.track([
              {
                kind: 'ui-action',
                action: {
                  kind: 'swapped-ingredient',
                  target: { kind: 'pairing', id },
                  position: index,
                  swapped_in: {
                    kind: 'product',
                    id: targetIngredientId,
                  },
                  swapped_out: {
                    kind: 'product',
                    id: sourceIngredientId,
                  },
                },
              },
            ]);
            isModalEditor || isCustomModalEditor
              ? swap.swapPairing(targetPairing, handleUpdate)
              : swap.swapPairing(targetPairing);

            (!isModalEditor || isCustomModalEditor) && closeModal();
          }}
          pairingIngredientIds={modified.ingredients}
          isModalEditor={isModalEditor}
          pairingPoints={pairing.points}
          updateButton={
            isModalEditor && !isCustomModalEditor ? updateButton : undefined
          }
        />
      )}
    </SheetOrOverlay>
  );
}
